/* eslint-disable @typescript-eslint/no-unused-expressions */
import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import {generateFreeName} from '../../../../../utils/generateFreeName'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {
  deleteIfEmpty,
  ensurePipelineDraftEntry,
  getPipelineDraftEntry,
} from './EditPipelinePage.slices.utils'

export const setParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    id: string
    savedName: string
    value: string
    branch: string
  }>
> = (state, action) => {
  const {id, savedName, value, branch} = action.payload
  const {draft} = getPipelineDraftEntry(state, id, branch) ?? {}
  if (draft != null) {
    draft.settings.parameters ??= {}
    draft.settings.parameters[savedName] = value
  }
}

export const renameParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    id: string
    name: string
    savedName: string
    branch: string
  }>
> = (state, action) => {
  const {id, name, savedName, branch} = action.payload

  if (name !== savedName) {
    const pipeline = ensurePipelineDraftEntry(state, id, branch)
    pipeline.renamed ??= {}
    pipeline.renamed!.parameters ??= {}
    pipeline.renamed!.parameters![savedName] = name
  } else if (getPipelineDraftEntry(state, id, branch)?.renamed?.parameters?.[savedName] != null) {
    const pipeline = getPipelineDraftEntry(state, id, branch)!
    delete pipeline.renamed!.parameters![savedName]
    deleteIfEmpty(pipeline.renamed, 'parameters') && deleteIfEmpty(pipeline, 'renamed')
  }
}

export const deleteParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; savedName: string; branch: string}>
> = (state, action) => {
  const {id, savedName, branch} = action.payload

  if (getPipelineDraftEntry(state, id, branch)?.added?.parameters?.[savedName]) {
    const pipeline = getPipelineDraftEntry(state, id, branch)!
    delete pipeline.draft?.settings.parameters?.[savedName]
    delete pipeline.added!.parameters![savedName]
    deleteIfEmpty(pipeline.added, 'parameters') && deleteIfEmpty(pipeline, 'added')
    delete pipeline.renamed!.parameters![savedName]
    deleteIfEmpty(pipeline.renamed, 'parameters') && deleteIfEmpty(pipeline, 'renamed')
  } else {
    const pipeline = ensurePipelineDraftEntry(state, id, branch)
    pipeline.deleted ??= {}
    pipeline.deleted!.parameters ??= []
    pipeline.deleted!.parameters!.push(savedName)
  }
}

export const restoreParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; savedName: string; branch: string}>
> = (state, action) => {
  const {id, savedName, branch} = action.payload
  const {deleted} = getPipelineDraftEntry(state, id, branch) ?? {}
  if (deleted?.parameters?.includes(savedName)) {
    deleted.parameters = deleted.parameters.filter(item => item !== savedName)
    deleteIfEmpty(deleted, 'parameters') &&
      deleteIfEmpty(getPipelineDraftEntry(state, id, branch), 'deleted')
  }
}

export const addParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; branch: string}>
> = (state, action) => {
  const {id, branch} = action.payload
  const pipeline = getPipelineDraftEntry(state, id, branch)
  const {draft} = pipeline ?? {}
  if (draft != null) {
    draft.settings.parameters ??= {}
    const key = generateFreeName({
      getName: iteration => `parameter_${iteration}`,
      usedNames: Object.keys(draft.settings.parameters),
      errorMessage: 'Cannot generate unique parameter key',
    })
    draft.settings.parameters[key] = ''

    renameParameter(state, {type: '', payload: {id, name: '', savedName: key, branch}})
    pipeline!.added ??= {}
    pipeline!.added!.parameters ??= {}
    pipeline!.added!.parameters![key] = true
  }
}

export const clearAddedParameters: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; branch: string}>
> = (state, action) => {
  const {id, branch} = action.payload
  const {added, draft} = getPipelineDraftEntry(state, id, branch) ?? {}
  if (added?.parameters != null) {
    Object.keys(added.parameters).forEach(key => {
      delete draft?.settings.parameters?.[key]
    })
    delete added.parameters
    deleteIfEmpty(getPipelineDraftEntry(state, id, branch), 'added')
  }
}

export const applyAddedParameters: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; branch: string}>
> = (state, action) => {
  const {id, branch} = action.payload
  const {added} = getPipelineDraftEntry(state, id, branch) ?? {}
  if (added?.parameters != null) {
    delete added.parameters
    deleteIfEmpty(getPipelineDraftEntry(state, id, branch), 'added')
  }
}
